import { exitKey, nextKey, prevKey } from './navigate';
import type { WizardIndex } from './types';

export interface WizardFooterState {
  /** False only when there is nothing to show at all — a root menu step. */
  showFooter: boolean;
  showBack: boolean;
  showPrimary: boolean;
  primaryLabel: string;
  backLabel: string;
  /**
   * Whether there is a step after this one — i.e. whether the primary ADVANCES or SUBMITS.
   *
   * Exposed rather than left internal (it already decides `primaryLabel`) because a shell needs the
   * same fact to decide what its primary DOES, and the only alternative is hardcoding the last step's
   * key: `AudienceActivationSheet` had `wiz.stepKey === 'sync-schedule'`, which appending one step to
   * the tree would leave submitting under a button reading "Next".
   */
  hasNext: boolean;
}

/**
 * Derive the footer from the tree rather than from each step's own render.
 *
 * `activationWizard.ts` used to derive `showFooter` / `showNextAction` from `kind` itself; this put
 * that rule in one place so a step cannot forget it, and added the sub-flow case: a step inside a sub-flow
 * always has a way back, even when it is the sub-flow's first step and has no structural parent to
 * return to within the flow.
 */
export function footerState<K extends string>(
  index: WizardIndex<K>,
  key: K,
  options: { doneLabel?: string } = {},
): WizardFooterState {
  const step = index.byKey.get(key);
  const isMenu = step?.kind === 'menu';
  const canGoBack = prevKey(index, key) !== null || exitKey(index, key) !== null;
  const hasNext = nextKey(index, key) !== null;

  return {
    showFooter: canGoBack || !isMenu,
    showBack: canGoBack,
    showPrimary: !isMenu,
    primaryLabel: hasNext ? 'Next' : (options.doneLabel ?? 'Done'),
    backLabel: 'Back',
    hasNext,
  };
}
